/**
 * Instant Beat v3 — REAL drum samples + REAL piano + synthesised bass/melodic layers.
 * Genre-aware arrangements. Each genre kit picks its own drum sample set so
 * the sound source is recordings of actual instruments (not sine waves).
 *
 * First call to synthesizeBeat() takes 1-3s to fetch the kit (≈ 6 mp3s).
 * Subsequent calls render in <100ms because samples are cached in memory.
 *
 * Output: stereo AudioBuffer, ~3-5 minutes long with full song structure
 * (intro → verse → chorus → verse 2 → chorus 2 → bridge → final chorus → outro).
 */

import {
  loadDrumKit, loadPiano, loadInstrument, nearestSample, nearestPianoSample, writePitchedSample,
} from "./sampleBank.js";

// ─────────────────────────────────────────────────────────────────────────────
//  Synthesis primitives
// ─────────────────────────────────────────────────────────────────────────────

function envelope(t, attack, decay, sustain, release, dur) {
  if (t < attack) return t / attack;
  if (t < attack + decay) return 1 - ((t - attack) / decay) * (1 - sustain);
  if (t < dur - release) return sustain;
  if (t < dur) return sustain * (1 - (t - (dur - release)) / release);
  return 0;
}

function jitter(amount) { return (Math.random() * 2 - 1) * amount; }
function velocity(min = 0.85, max = 1.0) { return min + Math.random() * (max - min); }
function noteToFreq(midi) { return 440 * Math.pow(2, (midi - 69) / 12); }

// Seedable mulberry32 PRNG — used by reroll-variant generation so each reroll
// gets a different but DETERMINISTIC chord/lead/swing pattern.
function makeRng(seed) {
  let t = seed >>> 0;
  return function rng() {
    t |= 0; t = t + 0x6D2B79F5 | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = r + Math.imul(r ^ (r >>> 7), 61 | r) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Soft clip — gentle saturation so additive layers don't peak ugly.
function softClip(x) {
  return Math.tanh(x * 1.2) * 0.85;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Drum synthesis — tuned per kit
// ─────────────────────────────────────────────────────────────────────────────

function writeKick(buf, startSample, sampleRate, opts = {}) {
  const { gain = 1, pitch = 120, endPitch = 40, decay = 0.18, sub = 0 } = opts;
  const dur = decay;
  const len = Math.floor(dur * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  const startP = pitch + jitter(4);
  const endP = endPitch + jitter(2);
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    const freq = startP - (startP - endP) * Math.min(1, t / 0.05);
    const env = envelope(t, 0.001, 0.04, 0.0, decay - 0.06, dur);
    // Optional sub-layer (long sine tail at low freq) — used by 808/Trap kits.
    const subLayer = sub > 0 ? Math.sin(2 * Math.PI * (endP * 0.5) * t) * sub * envelope(t, 0.001, 0.01, 0.5, decay - 0.04, dur) : 0;
    const sample = (Math.sin(2 * Math.PI * freq * t) + subLayer) * env * gain;
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += sample;
      if (R) R[idx] += sample;
    }
  }
}

function writeSnare(buf, startSample, sampleRate, opts = {}) {
  const { gain = 0.9, tone = 220, decay = 0.16, snap = 1.0, pan = 0 } = opts;
  const dur = decay;
  const len = Math.floor(dur * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  const panL = Math.cos((pan + 1) * Math.PI / 4);
  const panR = Math.sin((pan + 1) * Math.PI / 4);
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    const nL = (Math.random() * 2 - 1) * 0.65 * snap;
    const nR = R ? (Math.random() * 2 - 1) * 0.65 * snap : 0;
    const ton = Math.sin(2 * Math.PI * (tone + jitter(6)) * t) * 0.35;
    const env = envelope(t, 0.001, 0.03, 0.1, decay - 0.04, dur);
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += (nL + ton) * env * gain * panL * 1.41;
      if (R) R[idx] += (nR + ton) * env * gain * panR * 1.41;
    }
  }
}

// Cross-stick / rim-click — used by jazz, bossa, country brushes
function writeRim(buf, startSample, sampleRate, gain = 0.5) {
  const dur = 0.04;
  const len = Math.floor(dur * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    const click = Math.sin(2 * Math.PI * (800 + jitter(50)) * t) * 0.7;
    const noise = (Math.random() * 2 - 1) * 0.4;
    const env = envelope(t, 0.0005, 0.01, 0.05, 0.02, dur);
    const sample = (click + noise) * env * gain;
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += sample * 0.8;
      if (R) R[idx] += sample * 1.0;
    }
  }
}

function writeHat(buf, startSample, sampleRate, opts = {}) {
  const { gain = 0.32, open = false, brightness = 1.0, pan = 0 } = opts;
  const dur = open ? 0.22 : 0.045;
  const len = Math.floor(dur * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  let prevL = 0;
  let prevR = 0;
  const panL = Math.cos((pan + 1) * Math.PI / 4);
  const panR = Math.sin((pan + 1) * Math.PI / 4);
  const hpf = 1 - brightness * 0.3; // higher brightness → less low cut → no wait, more bright = less averaging
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    const nL = Math.random() * 2 - 1;
    const nR = Math.random() * 2 - 1;
    const fL = nL - prevL * hpf; prevL = nL;
    const fR = nR - prevR * hpf; prevR = nR;
    const env = envelope(t, 0.001, open ? 0.06 : 0.01, 0.05, open ? 0.14 : 0.03, dur);
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += fL * env * gain * panL * 1.41;
      if (R) R[idx] += fR * env * gain * panR * 1.41;
    }
  }
}

function writeShaker(buf, startSample, sampleRate, gain = 0.18) {
  const dur = 0.08;
  const len = Math.floor(dur * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  let prevL = 0;
  let prevR = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    const nL = Math.random() * 2 - 1;
    const nR = Math.random() * 2 - 1;
    const fL = nL - prevL * 0.85; prevL = nL;
    const fR = nR - prevR * 0.85; prevR = nR;
    const env = envelope(t, 0.002, 0.02, 0.2, 0.04, dur);
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += fL * env * gain;
      if (R) R[idx] += fR * env * gain;
    }
  }
}

function writeClap(buf, startSample, sampleRate, gain = 0.7) {
  for (let burst = 0; burst < 3; burst++) {
    const offset = Math.floor(burst * 0.012 * sampleRate);
    const dur = 0.05;
    const len = Math.floor(dur * sampleRate);
    const L = buf.getChannelData(0);
    const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
    for (let i = 0; i < len; i++) {
      const t = i / sampleRate;
      const nL = (Math.random() * 2 - 1) * 0.5;
      const nR = R ? (Math.random() * 2 - 1) * 0.5 : 0;
      const env = envelope(t, 0.001, 0.01, 0.2, 0.03, dur);
      const idx = startSample + offset + i;
      const bg = burst === 2 ? 1 : 0.7;
      if (idx < L.length) {
        L[idx] += nL * env * gain * bg;
        if (R) R[idx] += nR * env * gain * bg;
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Bass synthesis — multiple timbres per genre
// ─────────────────────────────────────────────────────────────────────────────

function writeBass(buf, startSample, sampleRate, freq, durSec, opts = {}) {
  const { gain = 0.5, timbre = "synth" } = opts;
  const len = Math.floor(durSec * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  // Per-call state for the resonant filter timbres (acid, wobble). Allocating
  // locals here keeps the hot inner-loop branch free of `let` re-declaration.
  let svfLp = 0, svfBp = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    let sample = 0;
    if (timbre === "808") {
      // 808 sub-bass — pure sine with long decay, very low
      const env = envelope(t, 0.005, 0.05, 0.85, durSec * 0.3, durSec);
      sample = Math.sin(2 * Math.PI * freq * 0.5 * t) * env * gain;
    } else if (timbre === "upright") {
      // Upright bass — sine + subtle 2nd harmonic, woody attack
      const env = envelope(t, 0.01, 0.08, 0.5, 0.12, durSec);
      const fund = Math.sin(2 * Math.PI * freq * t);
      const h2 = Math.sin(2 * Math.PI * freq * 2 * t) * 0.2;
      sample = (fund + h2) * env * gain * 0.9;
    } else if (timbre === "picked") {
      // Picked electric bass — square+sine with pluck attack
      const env = envelope(t, 0.002, 0.04, 0.4, 0.08, durSec);
      const sq = Math.sign(Math.sin(2 * Math.PI * freq * t));
      const sn = Math.sin(2 * Math.PI * freq * t);
      sample = (sq * 0.3 + sn * 0.7) * env * gain;
    } else if (timbre === "distorted") {
      // Distorted bass — saturated square wave
      const env = envelope(t, 0.003, 0.05, 0.7, 0.06, durSec);
      const sq = Math.sign(Math.sin(2 * Math.PI * freq * t));
      sample = softClip(sq * 1.5) * env * gain;
    } else if (timbre === "acid") {
      // Acid bass (TB-303) — square+saw through high-Q resonant lowpass,
      // env modulates cutoff from 3× to 18× of fundamental. The classic
      // "squelch" comes from this filter sweep, not the oscillator.
      const env = envelope(t, 0.003, 0.04, 0.5, 0.05, durSec);
      const sq = Math.sign(Math.sin(2 * Math.PI * freq * t)) * 0.6;
      const saw = (2 * (freq * t - Math.floor(0.5 + freq * t))) * 0.5;
      const raw = sq + saw;
      const cutoff = freq * (3 + env * 15);
      const f = 2 * Math.sin(Math.PI * cutoff / sampleRate);
      svfLp = svfLp + f * svfBp;
      svfBp = svfBp + f * (raw - svfLp - 0.85 * svfBp);
      sample = (svfLp / (1 + Math.abs(svfLp) * 0.4)) * env * gain;
    } else if (timbre === "moog") {
      // Moog bass — square + sub-octave square through a smooth one-pole
      // LPF that opens slightly with env. Warm, thick low-end.
      const env = envelope(t, 0.005, 0.06, 0.65, 0.12, durSec);
      const sq = Math.sign(Math.sin(2 * Math.PI * freq * t)) * 0.55;
      const sub = Math.sign(Math.sin(2 * Math.PI * freq * 0.5 * t)) * 0.25;
      const raw = sq + sub;
      const cutoff = freq * (4 + env * 4);
      const rc = 1 / (2 * Math.PI * cutoff);
      const alpha = (1 / sampleRate) / (rc + 1 / sampleRate);
      svfLp = svfLp + alpha * (raw - svfLp);
      sample = svfLp * env * gain * 0.95;
    } else if (timbre === "fm") {
      // FM bass (DX7-style) — 2-op FM with integer modulator ratio. Index
      // ramps from 12 → 4 with envelope, giving the iconic bell-attack →
      // sine-settle character. Skips any filter — FM tone is already shaped.
      const env = envelope(t, 0.001, 0.04, 0.4, 0.06, durSec);
      const modIndex = 4 + env * 8;
      const w = 2 * Math.PI * freq * t;
      const mod = Math.sin(w) * modIndex;
      sample = Math.sin(w + mod) * env * gain * 0.9;
    } else if (timbre === "pluck") {
      // Pluck bass — triangle with very fast envelope (near-zero sustain).
      // Pop / EDM / future-bass pluck. 2nd harmonic adds tonal definition.
      const env = envelope(t, 0.001, 0.04, 0.08, 0.04, durSec);
      const tri = 2 * Math.abs(2 * (freq * t - Math.floor(0.5 + freq * t))) - 1;
      const second = Math.sin(2 * Math.PI * 2 * freq * t) * 0.2;
      sample = (tri * 0.75 + second) * env * gain;
    } else {
      // Default synth bass — square + sine hybrid
      const env = envelope(t, 0.005, 0.05, 0.7, 0.08, durSec);
      const sq = Math.sign(Math.sin(2 * Math.PI * freq * t));
      const sn = Math.sin(2 * Math.PI * freq * t);
      sample = (sq * 0.45 + sn * 0.55) * env * gain;
    }
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += sample;
      if (R) R[idx] += sample;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Chord / pad / lead synthesis — voicings define the genre
// ─────────────────────────────────────────────────────────────────────────────

function writeChord(buf, startSample, sampleRate, freqs, durSec, opts = {}) {
  const { gain = 0.25, voicing = "piano", pan = 0 } = opts;
  const len = Math.floor(durSec * sampleRate);
  const L = buf.getChannelData(0);
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : null;
  const panL = Math.cos((pan + 1) * Math.PI / 4);
  const panR = Math.sin((pan + 1) * Math.PI / 4);

  for (let i = 0; i < len; i++) {
    const t = i / sampleRate;
    let summed = 0;
    let summedR = 0;

    for (let n = 0; n < freqs.length; n++) {
      const f = freqs[n];
      let voice = 0;
      let voiceR = 0;

      if (voicing === "piano") {
        // Plucky exponential decay — sine + slight 2nd harmonic
        const env = envelope(t, 0.005, 0.3, 0.2, durSec * 0.4, durSec);
        voice = (Math.sin(2 * Math.PI * f * t) + 0.3 * Math.sin(2 * Math.PI * f * 2 * t)) * env;
        voiceR = voice;
      } else if (voicing === "rhodes") {
        // Rhodes — bell-like multi-harmonic with slow attack
        const env = envelope(t, 0.015, 0.4, 0.3, durSec * 0.4, durSec);
        const fund = Math.sin(2 * Math.PI * f * t);
        const bell = Math.sin(2 * Math.PI * f * 2 * t) * 0.4 * Math.exp(-t * 3);
        const fifth = Math.sin(2 * Math.PI * f * 1.5 * t) * 0.15;
        voice = (fund + bell + fifth) * env;
        voiceR = voice;
      } else if (voicing === "organ") {
        // Hammond-ish organ — fundamental + octave + 5th additive
        const env = envelope(t, 0.01, 0.05, 0.85, 0.05, durSec);
        const fund = Math.sin(2 * Math.PI * f * t);
        const oct = Math.sin(2 * Math.PI * f * 2 * t) * 0.5;
        const fifth = Math.sin(2 * Math.PI * f * 3 * t) * 0.3;
        voice = (fund + oct + fifth) * env;
        voiceR = voice;
      } else if (voicing === "power") {
        // Distorted power chord — saturated square
        const env = envelope(t, 0.003, 0.1, 0.7, 0.1, durSec);
        const sq = Math.sign(Math.sin(2 * Math.PI * f * t));
        voice = softClip(sq * 1.8) * env;
        voiceR = voice;
      } else if (voicing === "pad") {
        // Slow pad — slow attack/release, detuned voices for movement
        const env = envelope(t, 0.15, 0.2, 0.7, 0.3, durSec);
        const v1 = Math.sin(2 * Math.PI * f * t);
        const v2 = Math.sin(2 * Math.PI * f * 1.005 * t); // slight detune
        const v3 = Math.sin(2 * Math.PI * f * 0.995 * t);
        voice = (v1 + v2 + v3) / 3 * env;
        voiceR = voice;
      } else if (voicing === "pluck") {
        // Plucked string — fast attack, fast decay, brighter
        const env = envelope(t, 0.001, 0.15, 0.05, 0.05, durSec);
        const fund = Math.sin(2 * Math.PI * f * t);
        const h2 = Math.sin(2 * Math.PI * f * 2 * t) * 0.5 * Math.exp(-t * 6);
        voice = (fund + h2) * env;
        voiceR = voice;
      } else if (voicing === "nylon") {
        // Nylon guitar — like pluck but softer, less bright
        const env = envelope(t, 0.005, 0.25, 0.1, 0.1, durSec);
        const fund = Math.sin(2 * Math.PI * f * t);
        const h2 = Math.sin(2 * Math.PI * f * 2 * t) * 0.25 * Math.exp(-t * 4);
        voice = (fund + h2) * env;
        voiceR = voice;
      } else if (voicing === "stab") {
        // Short organ-like stab — fast on, fast off
        const env = envelope(t, 0.005, 0.03, 0.8, 0.05, durSec);
        const fund = Math.sin(2 * Math.PI * f * t);
        const sq = Math.sign(Math.sin(2 * Math.PI * f * t)) * 0.3;
        voice = (fund + sq) * env;
        voiceR = voice;
      } else if (voicing === "string") {
        // Bowed string pad — slow attack with vibrato
        const env = envelope(t, 0.08, 0.1, 0.85, 0.2, durSec);
        const vib = 1 + Math.sin(2 * Math.PI * 5.5 * t) * 0.008;
        const fund = Math.sin(2 * Math.PI * f * vib * t);
        const h2 = Math.sin(2 * Math.PI * f * 2 * vib * t) * 0.4;
        voice = (fund + h2) * env;
        voiceR = voice;
      } else {
        // Fallback — simple sine
        const env = envelope(t, 0.01, 0.1, 0.5, 0.1, durSec);
        voice = Math.sin(2 * Math.PI * f * t) * env;
        voiceR = voice;
      }

      // Spread chord notes slightly across stereo for width
      const noteOffset = (n - (freqs.length - 1) / 2) * 0.15;
      const noteL = Math.cos((noteOffset + 1) * Math.PI / 4);
      const noteR = Math.sin((noteOffset + 1) * Math.PI / 4);
      summed += voice * noteL;
      summedR += voiceR * noteR;
    }

    // Normalize by chord size so 4-note chords aren't 4x louder than 1-note
    const norm = 1 / Math.sqrt(freqs.length);
    const idx = startSample + i;
    if (idx < L.length) {
      L[idx] += summed * gain * norm * panL * 1.41;
      if (R) R[idx] += summedR * gain * norm * panR * 1.41;
    }
  }
}

// Lead/melody — single-note version of writeChord
function writeLead(buf, startSample, sampleRate, freq, durSec, opts = {}) {
  writeChord(buf, startSample, sampleRate, [freq], durSec, opts);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Music theory — chord builders + progression DSL
// ─────────────────────────────────────────────────────────────────────────────

// MIDI offsets for chord types relative to the root.
const CHORD_TYPES = {
  maj:   [0, 4, 7],
  min:   [0, 3, 7],
  dim:   [0, 3, 6],
  maj7:  [0, 4, 7, 11],
  min7:  [0, 3, 7, 10],
  dom7:  [0, 4, 7, 10],
  sus4:  [0, 5, 7],
  power: [0, 7, 12],
};

// Build chord MIDI notes from a root MIDI note + chord type.
function buildChord(rootMidi, type = "maj") {
  const offsets = CHORD_TYPES[type] || CHORD_TYPES.maj;
  return offsets.map((o) => rootMidi + o);
}

// Convert key name → semitone offset from C.
const KEY_OFFSETS = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };

function keyToOffset(key) {
  const clean = (key || "C").replace(/\s+(?:major|minor)$/i, "");
  return KEY_OFFSETS[clean] ?? 0;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Genre kits — the actual sonic identity of each style
// ─────────────────────────────────────────────────────────────────────────────
//
//  Each kit defines:
//    tempo:  preferred BPM (overrides session tempo)
//    drums:  { kickPattern, snarePattern, hatPattern, hatOpenSteps?, rim?, shaker?, clap? }
//    kickOpts, snareOpts, hatOpts:  per-element synthesis params (timbre tuning)
//    bassOpts:  { timbre, octave }
//    chordVoicing:  voicing keyword for writeChord
//    progression:  array of [scaleDegree, chordType, durationInBeats]
//                  scale degrees are relative to the major scale offset from key root
//    leadProgression?:  optional [midiOffset, durBeats] array for a topline melody
//    chordOctave:  base MIDI octave for chords (4 = middle C area)
//    bassPattern:  per-bar 16-step grid mapping step→scale-degree (root of current chord by default)
//    intensity:  per-section multiplier {intro, verse, chorus, bridge, outro}
//
// Genre catalog — exported so unit tests can verify the bass/drum mapping
// without going through a full 3-minute synthesizeBeat render.
export const KITS = {
  // ─── Hip-Hop / Rap ───
  "Trap": {
    tempo: 140,
    drumKitSamples: "synth-808", // TR-808 Trap — synthesised, perfect genre match
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 0,0,0,0, 0,1,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,1,1,1, 1,1,2,1, 1,1,0,1, 1,2,1,1],
    },
    bassTimbre: "808",
    chordVoicing: "piano",
    progression: [[0, "min7", 4], [-2, "min7", 4], [-3, "maj7", 4], [-5, "maj7", 4]],
    chordOctave: 4,
  },
  "Drill": {
    tempo: 142,
    drumKitSamples: "CR78",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 0,0,0,1, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,1,0],
      hat:   [1,0,1,1, 0,1,1,0, 1,1,0,1, 1,0,1,1],
    },
    bassTimbre: "808",
    chordVoicing: "piano",
    progression: [[0, "min", 4], [-3, "min", 4], [-2, "min7", 4], [-5, "maj", 4]],
    chordOctave: 4,
  },
  "Boom Bap": {
    tempo: 90,
    drumKitSamples: "synth-lofi", // Lo-Fi Boom-Bap — synthesised, warm + filtered
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "upright",
    chordVoicing: "piano",
    progression: [[0, "maj7", 4], [5, "min7", 4], [-3, "maj7", 4], [-5, "dom7", 4]],
    chordOctave: 4,
  },
  "Lo-fi Hip-Hop": {
    tempo: 78,
    drumKitSamples: "synth-lofi", // Lo-Fi Boom-Bap — perfect genre match
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,0,1, 0,1,0,0, 1,0,0,1, 0,1,0,0],
    },
    bassTimbre: "moog", // warm filtered analog — staple lo-fi bass character
    chordVoicing: "piano",
    progression: [[0, "maj7", 4], [-3, "maj7", 4], [5, "min7", 4], [-2, "min7", 4]],
    chordOctave: 4,
  },
  // ─── Electronic ───
  "House": {
    tempo: 124,
    drumKitSamples: "synth-909", // TR-909 House — the kit that DEFINED house music
    drums: {
      kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
      clap:  [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [0,0,1,0, 0,0,1,0, 0,0,1,0, 0,0,1,0],
      hatOpenSteps: [2, 6, 10, 14],
    },
    bassTimbre: "moog", // Warm Moog bass — the staple house bass timbre
    chordVoicing: "piano",
    progression: [[0, "maj7", 4], [4, "dom7", 4], [5, "min7", 4], [-3, "maj7", 4]],
    chordOctave: 4,
    chordPattern: "stab",
  },
  "Deep House": {
    tempo: 122,
    drumKitSamples: "synth-909", // TR-909 — house standard
    drums: {
      kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
      clap:  [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [0,0,1,0, 0,0,1,0, 0,0,1,0, 0,0,1,0],
      hatOpenSteps: [2, 6, 10, 14],
    },
    bassTimbre: "moog", // Warm filtered Moog — the deep house signature
    chordVoicing: "piano",
    progression: [[0, "min7", 8], [-3, "maj7", 8]],
    chordOctave: 4,
  },
  "Techno": {
    tempo: 130,
    drumKitSamples: "Techno",
    drums: {
      kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
      snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1],
      hat:   [0,1,0,1, 0,1,0,1, 0,1,0,1, 0,1,0,1],
    },
    bassTimbre: "acid",
    chordVoicing: "stab",
    progression: [[0, "min", 8], [-2, "min", 8]],
    chordOctave: 5,
  },
  "Drum & Bass": {
    tempo: 174,
    drumKitSamples: "synth-dnb", // DnB / Jungle — hard kick, bright snare, fast hat
    drums: {
      kick:  [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1],
    },
    bassTimbre: "distorted",
    chordVoicing: "pad",
    progression: [[0, "min", 8], [-3, "maj7", 8]],
    chordOctave: 4,
  },
  "Synth-Pop": {
    tempo: 116,
    drumKitSamples: "LINN", // classic 80s LinnDrum sound
    drums: {
      kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [0,1,0,1, 0,1,0,1, 0,1,0,1, 0,1,0,1],
    },
    bassTimbre: "fm", // DX7 FM bass — the iconic 80s synth-pop bass tone
    chordVoicing: "piano",
    progression: [[0, "maj", 4], [4, "maj", 4], [5, "min", 4], [-3, "maj", 4]],
    chordOctave: 4,
  },
  // ─── Rock / Metal ───
  "Classic Rock": {
    tempo: 118,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "picked",
    chordVoicing: "power",
    progression: [[0, "power", 4], [-3, "power", 4], [5, "power", 4], [-2, "power", 4]],
    chordOctave: 3,
  },
  "Indie Rock": {
    tempo: 118,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,0,1, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,1,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "picked",
    chordVoicing: "piano",
    progression: [[0, "maj", 4], [4, "maj", 4], [-3, "maj", 4], [5, "min", 4]],
    chordOctave: 3,
  },
  "Punk Rock": {
    tempo: 165,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1],
    },
    bassTimbre: "distorted",
    chordVoicing: "power",
    progression: [[0, "power", 4], [5, "power", 4], [-3, "power", 4], [-5, "power", 4]],
    chordOctave: 3,
  },
  "Heavy Metal": {
    tempo: 140,
    drumKitSamples: "synth-industrial", // distorted kick + metallic hits
    drums: {
      kick:  [1,1,0,0, 1,0,0,0, 1,1,0,0, 0,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "distorted",
    chordVoicing: "power",
    progression: [[0, "power", 2], [-3, "power", 2], [-5, "power", 4], [0, "power", 4]],
    chordOctave: 3,
  },
  // ─── Pop ───
  "Mainstream Pop": {
    tempo: 110,
    drumKitSamples: "R8",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "pluck", // bright triangle pluck — modern pop signature
    chordVoicing: "piano",
    progression: [[0, "maj", 4], [4, "maj", 4], [5, "min", 4], [-3, "maj", 4]],
    chordOctave: 4,
  },
  "Indie Pop": {
    tempo: 112,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "picked",
    chordVoicing: "piano",
    progression: [[0, "maj7", 4], [5, "min7", 4], [-3, "maj7", 4], [-5, "maj7", 4]],
    chordOctave: 4,
  },
  // ─── Country / Folk ───
  "Classic Country": {
    tempo: 96,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "upright",
    chordVoicing: "piano",
    progression: [[0, "maj", 4], [-5, "maj", 4], [4, "maj", 4], [0, "maj", 4]],
    chordOctave: 3,
    chordPattern: "strum",
  },
  "Indie Folk": {
    tempo: 96,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
      hat:   [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
    },
    bassTimbre: "upright",
    chordVoicing: "piano",
    progression: [[0, "maj", 4], [-3, "maj", 4], [5, "min", 4], [4, "maj", 4]],
    chordOctave: 3,
    chordPattern: "arpeggio",
  },
  // ─── Latin / Reggae ───
  "Reggaeton": {
    tempo: 96,
    drumKitSamples: "Stark",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,1,0],
      snare: [0,0,1,0, 1,0,0,1, 0,0,1,0, 1,0,0,1],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    },
    bassTimbre: "synth",
    chordVoicing: "piano",
    progression: [[0, "min", 4], [-5, "maj", 4], [5, "min", 4], [4, "maj", 4]],
    chordOctave: 4,
  },
  "Bossa Nova": {
    tempo: 124,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,0,0],
    },
    bassTimbre: "upright",
    chordVoicing: "piano",
    progression: [[0, "maj7", 4], [3, "dom7", 4], [-3, "maj7", 4], [-1, "dom7", 4]],
    chordOctave: 4,
    chordPattern: "arpeggio",
  },
  "Roots Reggae": {
    tempo: 76,
    drumKitSamples: "acoustic-kit",
    drums: {
      kick:  [0,0,0,0, 0,0,1,0, 0,0,0,0, 0,0,1,0],
      hat:   [0,1,0,1, 0,1,0,1, 0,1,0,1, 0,1,0,1],
    },
    bassTimbre: "upright",
    chordVoicing: "organ",
    progression: [[0, "maj", 4], [5, "min", 4], [-3, "maj", 4], [4, "maj", 4]],
    chordOctave: 4,
    chordPattern: "skank",
  },
};

// Fallback for genres we haven't tuned yet.
KITS.__default = KITS["Mainstream Pop"];

// ─────────────────────────────────────────────────────────────────────────────
//  Song structure — arrangement of sections w/ per-section intensity
// ─────────────────────────────────────────────────────────────────────────────
//
//  Each section says: how many bars, drums on/off, chord layer on/off, bass on/off,
//  lead on/off, and chord octave shift for variety.
//

const STRUCTURE = [
  { name: "intro",        bars: 4,  drums: true,  bass: false, chord: true,  lead: false, gain: 0.7 },
  { name: "verse1",       bars: 16, drums: true,  bass: true,  chord: true,  lead: false, gain: 0.9 },
  { name: "chorus1",      bars: 16, drums: true,  bass: true,  chord: true,  lead: true,  gain: 1.0 },
  { name: "verse2",       bars: 16, drums: true,  bass: true,  chord: true,  lead: false, gain: 0.95 },
  { name: "chorus2",      bars: 16, drums: true,  bass: true,  chord: true,  lead: true,  gain: 1.0 },
  { name: "bridge",       bars: 8,  drums: false, bass: true,  chord: true,  lead: false, gain: 0.6, chordOctaveShift: 1 },
  { name: "finalChorus",  bars: 16, drums: true,  bass: true,  chord: true,  lead: true,  gain: 1.0 },
  { name: "outro",        bars: 4,  drums: true,  bass: true,  chord: true,  lead: false, gain: 0.5 },
];

const TOTAL_STRUCTURE_BARS = STRUCTURE.reduce((s, sec) => s + sec.bars, 0);

// ─────────────────────────────────────────────────────────────────────────────
//  Pattern rendering — turns a 16-step grid into actual sample writes
// ─────────────────────────────────────────────────────────────────────────────

function renderBar(buf, sampleRate, barStartTime, secondsPer16th, opts) {
  const {
    kit, sectionGain, includeDrums, includeBass, includeChord, includeLead,
    currentChord, currentBassRoot, keyOffsetSemitones, chordOctaveBase,
    chordPattern, secondsPerBeat,
    drumSamples,    // ← real drum sample buffers (or null for fallback)
    pianoBank,      // ← real piano sample bank (or null)
    bassBank,       // ← real electric bass sample bank (or null)
    chordBank,      // ← real guitar/organ/piano bank for chords (or null)
    leadBank,       // ← real guitar/piano bank for lead lines (or null)
  } = opts;

  const stepsPerBar = 16;
  const SWING = 0.012;
  const MICRO = 0.005;

  for (let step = 0; step < stepsPerBar; step++) {
    const isOffbeat = step === 2 || step === 6 || step === 10 || step === 14;
    const swing = isOffbeat ? SWING : 0;
    const stepTime = barStartTime + step * secondsPer16th + swing + jitter(MICRO);
    const startSample = Math.floor(Math.max(0, stepTime) * sampleRate);

    // ─── Drums (REAL SAMPLES — fall back to synth if a sample is missing)
    if (includeDrums) {
      const drums = kit.drums;
      if (drums.kick && drums.kick[step]) {
        const vel = velocity() * sectionGain;
        if (drumSamples?.kick) {
          writePitchedSample(buf, startSample, drumSamples.kick, 0, { gain: vel });
        } else {
          writeKick(buf, startSample, sampleRate, { ...kit.kickOpts, gain: (kit.kickOpts?.gain ?? 1) * vel });
        }
      }
      if (drums.snare && drums.snare[step]) {
        const vel = velocity(0.8) * sectionGain;
        if (drumSamples?.snare) {
          writePitchedSample(buf, startSample, drumSamples.snare, 0, { gain: vel * 0.9, pan: -0.15 });
        } else {
          writeSnare(buf, startSample, sampleRate, { ...kit.snareOpts, gain: (kit.snareOpts?.gain ?? 0.9) * vel });
        }
      }
      if (drums.rim && drums.rim[step]) {
        // Rim uses tom1 sample if available (short woody tone), else synth
        if (drumSamples?.tom1) {
          writePitchedSample(buf, startSample, drumSamples.tom1, 12, { gain: 0.4 * sectionGain });
        } else {
          writeRim(buf, startSample, sampleRate, 0.55 * sectionGain);
        }
      }
      if (drums.clap && drums.clap[step]) {
        // Tone.js doesn't ship a dedicated clap — use snare with brighter pitch
        if (drumSamples?.snare) {
          writePitchedSample(buf, startSample, drumSamples.snare, 4, { gain: 0.7 * sectionGain });
        } else {
          writeClap(buf, startSample, sampleRate, 0.65 * sectionGain);
        }
      }
      if (drums.hat && drums.hat[step]) {
        const panLeft = step % 2 === 0;
        const pan = panLeft ? -0.35 : 0.35;
        const code = drums.hat[step];
        if (code === 2) {
          // Trap hi-hat roll: 3 hits in this step
          const rollDur = secondsPer16th / 3;
          for (let r = 0; r < 3; r++) {
            const rTime = stepTime + r * rollDur;
            const rSample = Math.floor(rTime * sampleRate);
            if (drumSamples?.hihat) {
              writePitchedSample(buf, rSample, drumSamples.hihat, 0, { gain: 0.45 * sectionGain, pan, durationLimit: rollDur * 1.2 });
            } else {
              writeHat(buf, rSample, sampleRate, { ...kit.hatOpts, open: false, pan, gain: (kit.hatOpts?.gain ?? 0.32) * 0.7 * sectionGain });
            }
          }
        } else {
          const open = kit.drums.hatOpenSteps?.includes(step);
          if (drumSamples?.hihat) {
            const hatGain = velocity(0.75) * sectionGain * 0.55;
            writePitchedSample(buf, startSample, drumSamples.hihat, 0, {
              gain: hatGain, pan,
              // Closed hat: cut sample short to simulate damped close. Open: full length.
              durationLimit: open ? null : 0.08,
            });
          } else {
            writeHat(buf, startSample, sampleRate, { ...kit.hatOpts, open, pan, gain: (kit.hatOpts?.gain ?? 0.32) * velocity(0.75) * sectionGain });
          }
        }
      }
      if (drums.shaker && drums.shaker[step]) {
        writeShaker(buf, startSample, sampleRate, 0.16 * sectionGain);
      }
    }

    // ─── Bass — real electric bass samples if loaded, else synth fallback
    if (includeBass && step % 4 === 0) {
      const beat = step / 4;
      const bassMidi = currentBassRoot + keyOffsetSemitones - 24;
      if (bassBank && bassBank.length > 0) {
        const src = nearestSample(bassBank, bassMidi);
        if (src?.buf) {
          writePitchedSample(buf, startSample, src.buf, bassMidi - src.midi, {
            gain: 0.45 * sectionGain,
            durationLimit: secondsPerBeat * 0.95,
          });
          // Walking bass embellishment for upright/jazz feels — still apply with real sample
          if (beat === 3 && kit.bassTimbre === "upright") {
            const walkTime = stepTime + secondsPer16th * 2;
            const walkSample = Math.floor(walkTime * sampleRate);
            const walkMidi = bassMidi + 2;
            const walkSrc = nearestSample(bassBank, walkMidi);
            if (walkSrc?.buf) {
              writePitchedSample(buf, walkSample, walkSrc.buf, walkMidi - walkSrc.midi, {
                gain: 0.35 * sectionGain,
                durationLimit: secondsPerBeat * 0.45,
              });
            }
          }
        }
      } else {
        // Fallback to synth bass
        const freq = noteToFreq(bassMidi);
        writeBass(buf, startSample, sampleRate, freq, secondsPerBeat * 0.95, {
          gain: 0.55 * sectionGain,
          timbre: kit.bassTimbre || "synth",
        });
        if (beat === 3 && kit.bassTimbre === "upright") {
          const walkTime = stepTime + secondsPer16th * 2;
          const walkSample = Math.floor(walkTime * sampleRate);
          const walkFreq = noteToFreq(bassMidi + 2);
          writeBass(buf, walkSample, sampleRate, walkFreq, secondsPerBeat * 0.45, { gain: 0.42 * sectionGain, timbre: kit.bassTimbre });
        }
      }
    }

    // ─── Chord — uses real samples when chordBank available (piano/organ/guitar), else synth
    if (includeChord) {
      const chordRootMidi = currentChord.rootMidi + keyOffsetSemitones + chordOctaveBase * 12;
      const noteMidis = buildChord(chordRootMidi, currentChord.type);
      const freqs = noteMidis.map(noteToFreq);

      // Helper: emit one chord hit. Uses real samples if chordBank is available.
      const emitChord = (sample, durSec, gain) => {
        if (chordBank && chordBank.length > 0) {
          for (const midi of noteMidis) {
            const src = nearestSample(chordBank, midi);
            if (src?.buf) {
              writePitchedSample(buf, sample, src.buf, midi - src.midi, {
                gain: gain / Math.sqrt(noteMidis.length),
                pan: 0,
                durationLimit: durSec,
              });
            }
          }
        } else {
          writeChord(buf, sample, sampleRate, freqs, durSec, {
            gain, voicing: kit.chordVoicing, pan: 0,
          });
        }
      };

      if (chordPattern === "stab" && (step === 2 || step === 6 || step === 10 || step === 14)) {
        emitChord(startSample, secondsPer16th * 0.8, 0.28 * sectionGain);
      } else if (chordPattern === "skank" && (step === 4 || step === 12)) {
        emitChord(startSample, secondsPer16th * 0.7, 0.32 * sectionGain);
      } else if (chordPattern === "arpeggio") {
        if (step % 2 === 0) {
          const idx = (step / 2) % noteMidis.length;
          if (chordBank && chordBank.length > 0) {
            const src = nearestSample(chordBank, noteMidis[idx]);
            if (src?.buf) {
              writePitchedSample(buf, startSample, src.buf, noteMidis[idx] - src.midi, {
                gain: 0.32 * sectionGain,
                durationLimit: secondsPer16th * 1.8,
              });
            }
          } else {
            writeLead(buf, startSample, sampleRate, freqs[idx], secondsPer16th * 1.8, {
              gain: 0.32 * sectionGain, voicing: kit.chordVoicing,
            });
          }
        }
      } else if (chordPattern === "strum") {
        if (step === 0 || step === 8) {
          emitChord(startSample, secondsPerBeat * 1.8, 0.28 * sectionGain);
        } else if (step === 4 || step === 12) {
          emitChord(startSample, secondsPerBeat * 0.4, 0.16 * sectionGain);
        }
      } else if (step === 0) {
        const dur = (secondsPer16th * 16) * 0.95;
        emitChord(startSample, dur, 0.24 * sectionGain);
      }
    }

    // ─── Lead — uses real lead bank (piano or guitar) if available
    if (includeLead && (step === 0 || step === 8)) {
      const chordRootMidi = currentChord.rootMidi + keyOffsetSemitones + (chordOctaveBase + 2) * 12;
      const noteMidis = buildChord(chordRootMidi, currentChord.type);
      const idx = (step === 0) ? noteMidis.length - 1 : 1;
      const targetMidi = noteMidis[idx];
      const lb = leadBank || chordBank || pianoBank;
      if (lb && lb.length > 0) {
        const src = nearestSample(lb, targetMidi);
        if (src?.buf) {
          writePitchedSample(buf, startSample, src.buf, targetMidi - src.midi, {
            gain: 0.20 * sectionGain,
            durationLimit: secondsPerBeat * 1.6,
          });
        }
      } else {
        writeLead(buf, startSample, sampleRate, noteToFreq(targetMidi), secondsPerBeat * 1.6, {
          gain: 0.22 * sectionGain, voicing: "pluck",
        });
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main public entry — synthesize the whole song
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map a kit's chord voicing keyword to the real-instrument bank name.
 * Returns null if the voicing should stay synth (e.g. distorted power chords
 * — real guitar samples don't carry the same crunch as our synth model).
 */
function mapChordVoicingToInstrument(voicing) {
  switch (voicing) {
    case "piano":   return "piano";          // real Steinway grand
    case "organ":   return "organ";          // real Hammond-ish organ
    case "stab":    return "organ";          // techno stabs sound great on organ
    case "nylon":   return "guitar-nylon";   // real nylon-string guitar
    case "rhodes":  return "piano";          // Rhodes not available — piano substitute
    case "pluck":   return "guitar-electric"; // real clean electric guitar pluck
    case "string":  return "piano";          // strings not available — piano substitute
    case "pad":     return "organ";          // sustained organ works as a pad
    case "power":   return null;             // keep synth for distorted power chords
    default:        return null;
  }
}

/** Map a kit's bass timbre to a real-instrument bank name, or null for synth.
 *  Synth-only timbres (808 / acid / distorted / moog / fm / pluck) stay
 *  procedural — real bass samples can't capture those electronic / over-driven
 *  characters. "picked" / "synth" / "upright" load the real electric bass bank
 *  for the chord-following tonic notes (the synth path also fires in parallel
 *  for movement). */
function mapBassTimbreToInstrument(timbre) {
  switch (timbre) {
    case "synth":      return "bass-electric"; // real electric bass
    case "picked":     return "bass-electric"; // real electric bass with pluck
    case "upright":    return "bass-electric"; // we use electric as best-fit substitute
    case "808":        return null;            // keep 808 sub synth
    case "acid":       return null;            // keep TB-303-style synth
    case "distorted":  return null;            // keep distorted synth
    case "moog":       return null;            // keep Moog synth
    case "fm":         return null;            // keep FM synth
    case "pluck":      return null;            // keep pluck synth
    default:           return null;
  }
}

/**
 * Synthesize a full 3-minute genre-specific song using REAL drum samples
 * + REAL piano samples (loaded once, cached forever) + synthesised bass.
 *
 * @param {AudioContext} ctx
 * @param {object} opts — { genre, tempo (override), key, onLoadProgress, structureOverride }
 * @returns {Promise<AudioBuffer>}  Stereo, ready to load into a channel.
 */
export async function synthesizeBeat(ctx, { genre = "Mainstream Pop", tempo, key = "C", seed = 0, onLoadProgress, structureOverride } = {}) {
  const sampleRate = ctx.sampleRate;
  const kit = KITS[genre] || KITS.__default;
  // Seeded RNG drives variation across rerolls — first call (seed=0) uses
  // the default progression starting point + no transposition.
  const rng = makeRng((seed || 0) * 1009 + 1);

  const finalTempo = tempo || kit.tempo || 120;
  const secondsPerBeat = 60 / finalTempo;
  const secondsPer16th = secondsPerBeat / 4;
  const secondsPerBar = secondsPer16th * 16;

  const structure = structureOverride || STRUCTURE;
  const totalBars = structure.reduce((s, sec) => s + sec.bars, 0);
  const totalSeconds = totalBars * secondsPerBar;
  const totalSamples = Math.ceil(totalSeconds * sampleRate);

  // ─── Load real samples in parallel — drums always, then conditional banks
  onLoadProgress?.({ stage: "drums", kit: kit.drumKitSamples || "acoustic-kit" });
  const drumSamplesPromise = loadDrumKit(ctx, kit.drumKitSamples || "acoustic-kit")
    .catch((e) => { console.warn("[instantBeat] drum kit failed:", e); return null; });

  // Pick which melodic banks to load based on the kit's voicing + bass timbre.
  // chordBank: drives chord stabs / pads / strums (real piano / organ / guitar / nylon).
  // bassBank: real electric bass for synth/picked/distorted timbres; null for 808/upright (synth handles those better).
  // pianoBank: still loaded for any "piano" voicing (kept for lead lines too).
  const chordInstrument = mapChordVoicingToInstrument(kit.chordVoicing);
  const bassInstrument = mapBassTimbreToInstrument(kit.bassTimbre);

  const chordBankPromise = chordInstrument === "piano"
    ? loadPiano(ctx).catch(() => null)
    : (chordInstrument
        ? loadInstrument(ctx, chordInstrument).catch(() => null)
        : Promise.resolve(null));

  const bassBankPromise = bassInstrument
    ? loadInstrument(ctx, bassInstrument).catch(() => null)
    : Promise.resolve(null);

  // Lead bank — prefer the chord bank if it's a melodic instrument, else fall back to piano
  const needsPianoLead = chordInstrument !== "piano";
  const pianoBankPromise = needsPianoLead
    ? loadPiano(ctx).catch(() => null)
    : chordBankPromise; // piano is already loading

  const [drumSamples, chordBank, bassBank, pianoBank] = await Promise.all([
    drumSamplesPromise, chordBankPromise, bassBankPromise, pianoBankPromise,
  ]);
  onLoadProgress?.({ stage: "rendering" });

  const buf = ctx.createBuffer(2, totalSamples, sampleRate);
  const keyOffsetSemitones = keyToOffset(key);

  let cursorBar = 0;
  for (const section of structure) {
    const sectionGain = section.gain ?? 1.0;
    const chordOctaveBase = (kit.chordOctave ?? 4) + (section.chordOctaveShift ?? 0);
    const chordPattern = kit.chordPattern || null;

    const progression = kit.progression;
    // Reroll variation: rotate the progression starting point by a seeded amount
    // so different reroll seeds emphasize different chord moves.
    const progStartIdx = seed ? Math.floor(rng() * progression.length) : 0;
    let progIdx = progStartIdx;
    let beatsRemainingInChord = progression[progIdx][2];

    for (let b = 0; b < section.bars; b++) {
      const barStartTime = (cursorBar + b) * secondsPerBar;
      const [rootOffset, type] = progression[progIdx];
      const currentChord = { rootMidi: 60 + rootOffset, type };
      const currentBassRoot = 60 + rootOffset;

      renderBar(buf, sampleRate, barStartTime, secondsPer16th, {
        kit, sectionGain,
        includeDrums: section.drums,
        includeBass: section.bass,
        includeChord: section.chord,
        includeLead: section.lead,
        currentChord, currentBassRoot,
        keyOffsetSemitones, chordOctaveBase, chordPattern, secondsPerBeat,
        drumSamples, pianoBank, bassBank, chordBank,
        leadBank: pianoBank || chordBank,
      });

      beatsRemainingInChord -= 4;
      if (beatsRemainingInChord <= 0) {
        progIdx = (progIdx + 1) % progression.length;
        beatsRemainingInChord = progression[progIdx][2];
      }
    }

    cursorBar += section.bars;
  }

  // Final soft-clip — real samples + synth layered can exceed 1.0 in choruses.
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);
  for (let i = 0; i < totalSamples; i++) {
    L[i] = softClip(L[i] * 0.55);
    R[i] = softClip(R[i] * 0.55);
  }

  return buf;
}

/** Does the catalog have a dedicated tuned kit for this genre? */
export function hasInstantBeatPattern(genre) {
  return Object.prototype.hasOwnProperty.call(KITS, genre);
}

/** All catalogued genres (for tests / UI hints). */
export const INSTANT_BEAT_GENRES = Object.keys(KITS).filter((k) => k !== "__default");

/** Get the preferred tempo for a genre (for showing in the UI before generation). */
export function getGenreTempo(genre) {
  return (KITS[genre] || KITS.__default).tempo;
}


// ─────────────────────────────────────────────────────────────────────────────
//  Jam Mode — render drums + bass + chord pad as THREE SEPARATE buffers,
//  each 4-bars long at the chosen kit's native tempo, all sharing the same
//  progression. Caller loads each into its own channel so they can be muted /
//  soloed / EQ'd independently while staying perfectly in sync.
// ─────────────────────────────────────────────────────────────────────────────

const JAM_BARS = 4;

/**
 * Build three 4-bar buffers (drums / bass / chord pad) for a Jam Mode session.
 * All three share the same chord progression, tempo, and bar length so they
 * loop in lockstep.
 *
 * @returns Promise<{ tempo, key, genre, drums: AudioBuffer, bass: AudioBuffer, pad: AudioBuffer }>
 */
export async function synthesizeJamLayers(ctx, { genre, key = "C", onLoadProgress } = {}) {
  // Pick a tasteful genre if the caller didn't specify one.
  const pool = ["Mainstream Pop", "Boom Bap", "House", "Lo-fi Hip-Hop", "Indie Pop", "Synth-Pop", "Reggaeton", "Roots Reggae", "Classic Rock"];
  const finalGenre = genre || pool[Math.floor(Math.random() * pool.length)];
  const kit = KITS[finalGenre] || KITS.__default;
  const tempo = kit.tempo || 120;
  const secondsPerBeat = 60 / tempo;
  const secondsPer16th = secondsPerBeat / 4;
  const secondsPerBar = secondsPer16th * 16;
  const totalSeconds = JAM_BARS * secondsPerBar;
  const totalSamples = Math.ceil(totalSeconds * ctx.sampleRate);

  onLoadProgress?.({ stage: "loading", genre: finalGenre });
  const chordInstrument = mapChordVoicingToInstrument(kit.chordVoicing);
  const bassInstrument = mapBassTimbreToInstrument(kit.bassTimbre);

  const [drumSamples, chordBank, bassBank, pianoBank] = await Promise.all([
    loadDrumKit(ctx, kit.drumKitSamples || "acoustic-kit").catch(() => null),
    chordInstrument === "piano"
      ? loadPiano(ctx).catch(() => null)
      : (chordInstrument ? loadInstrument(ctx, chordInstrument).catch(() => null) : Promise.resolve(null)),
    bassInstrument ? loadInstrument(ctx, bassInstrument).catch(() => null) : Promise.resolve(null),
    loadPiano(ctx).catch(() => null),
  ]);

  onLoadProgress?.({ stage: "rendering" });

  const drumsBuf = ctx.createBuffer(2, totalSamples, ctx.sampleRate);
  const bassBuf  = ctx.createBuffer(2, totalSamples, ctx.sampleRate);
  const padBuf   = ctx.createBuffer(2, totalSamples, ctx.sampleRate);
  const keyOffsetSemitones = keyToOffset(key);
  const chordOctaveBase = kit.chordOctave ?? 4;
  const chordPattern = kit.chordPattern || null;
  const progression = kit.progression;

  // Walk through 4 bars; the kit's progression cycles internally.
  let progIdx = 0;
  let beatsRemainingInChord = progression[progIdx][2];

  for (let b = 0; b < JAM_BARS; b++) {
    const barStartTime = b * secondsPerBar;
    const [rootOffset, type] = progression[progIdx];
    const currentChord = { rootMidi: 60 + rootOffset, type };
    const currentBassRoot = 60 + rootOffset;

    // Drums-only
    renderBar(drumsBuf, ctx.sampleRate, barStartTime, secondsPer16th, {
      kit, sectionGain: 1.0,
      includeDrums: true, includeBass: false, includeChord: false, includeLead: false,
      currentChord, currentBassRoot, keyOffsetSemitones,
      chordOctaveBase, chordPattern, secondsPerBeat,
      drumSamples, pianoBank: null, bassBank: null, chordBank: null, leadBank: null,
    });

    // Bass-only
    renderBar(bassBuf, ctx.sampleRate, barStartTime, secondsPer16th, {
      kit, sectionGain: 1.0,
      includeDrums: false, includeBass: true, includeChord: false, includeLead: false,
      currentChord, currentBassRoot, keyOffsetSemitones,
      chordOctaveBase, chordPattern, secondsPerBeat,
      drumSamples: null, pianoBank: null, bassBank, chordBank: null, leadBank: null,
    });

    // Chord pad-only
    renderBar(padBuf, ctx.sampleRate, barStartTime, secondsPer16th, {
      kit, sectionGain: 0.9,
      includeDrums: false, includeBass: false, includeChord: true, includeLead: false,
      currentChord, currentBassRoot, keyOffsetSemitones,
      chordOctaveBase, chordPattern, secondsPerBeat,
      drumSamples: null, pianoBank, bassBank: null, chordBank, leadBank: null,
    });

    beatsRemainingInChord -= 4;
    if (beatsRemainingInChord <= 0) {
      progIdx = (progIdx + 1) % progression.length;
      beatsRemainingInChord = progression[progIdx][2];
    }
  }

  // Light soft-clip on each layer so loud individual tracks don't peak.
  for (const buf of [drumsBuf, bassBuf, padBuf]) {
    const L = buf.getChannelData(0);
    const R = buf.getChannelData(1);
    for (let i = 0; i < totalSamples; i++) {
      L[i] = softClip(L[i] * 0.7);
      R[i] = softClip(R[i] * 0.7);
    }
  }

  return { tempo, key, genre: finalGenre, drums: drumsBuf, bass: bassBuf, pad: padBuf };
}
